import { landAfterSignIn, type SignInLanding } from "@/components/auth/landAfterSignIn.ts";
import { NameStep } from "@/components/auth/NameStep.tsx";
import { passwordlessOptionsQuery } from "@/components/auth/passwordlessOptions.ts";
import { OkThxWordmark } from "@/components/icons/Logo.tsx";
import { WhatsAppIcon } from "@/components/icons/WhatsAppIcon.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Checkbox } from "@/components/ui/checkbox.tsx";
import { Label } from "@/components/ui/label.tsx";
import { api } from "@/lib/http.ts";
import { startFailureMessage, verifyFailureMessage } from "@/shared/passwordlessAuthCopy.ts";
import type {
  WhatsappCompleteResp,
  WhatsappStartResp,
  WhatsappStatusResp,
} from "@/types/whatsappSignIn.ts";
import { queryOptions, useMutation, useQuery } from "@tanstack/react-query";
import { LoaderCircle } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "wouter";

/** How often the page asks whether the message has arrived. */
const POLL_MS = 2000;

/** Keys the status this page finds on load, for a sign-in started before it. */
const RESUMED = "resumed";

/**
 * Where this tab keeps the link it was handed, so a reload before the message
 * is sent can show it again. Per tab, like the sign-in it belongs to; storage
 * that is unavailable just means a reloaded page offers to start again.
 */
const LINK_STORAGE_KEY = "whatsappSignIn.waLink";

function readStoredLink(): string | null {
  try {
    return sessionStorage.getItem(LINK_STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeLink(waLink: string): void {
  try {
    sessionStorage.setItem(LINK_STORAGE_KEY, waLink);
  } catch {
    // Without it a reload offers to start again, which still works.
  }
}

/**
 * Where this browser's sign-in stands. Polls while it waits and stops once the
 * answer can no longer change. The cookie `start` set is what identifies it to
 * the server; `key` (the link, or `RESUMED`) keys it here, so starting again
 * begins a fresh poll.
 */
const whatsappStatusQuery = (key: string) =>
  queryOptions({
    queryKey: ["whatsappSignIn", "status", key] as const,
    queryFn: async ({ signal }) =>
      (await api.get<WhatsappStatusResp>("/api/p/auth/whatsapp/status", { signal })).data,
    refetchInterval: (query) => (query.state.data?.status === "waiting" ? POLL_MS : false),
    // Someone who went to WhatsApp and came back should see the answer at once.
    refetchOnWindowFocus: "always",
    staleTime: 0,
    gcTime: 0,
  });

/**
 * Signing up with WhatsApp: the visitor sends okthx a code from WhatsApp, and
 * the number it arrives from is the one they have proved. Nothing is sent to
 * them first, so there is no number to type.
 *
 * Once the message arrives the page finishes exactly as the phone door does:
 * the session comes back as a cookie, a new person is asked their name, and
 * landing is a full navigation.
 */
export const WhatsAppAuth = () => {
  const [, setLocation] = useLocation();
  const [params] = useSearchParams();
  const returnTo = params.get("returnTo");
  const options = useQuery(passwordlessOptionsQuery);
  const doorOpen = options.data?.whatsappSignupEnabled === true;
  const [consent, setConsent] = useState(false);
  const [askName, setAskName] = useState<SignInLanding | null>(null);
  const [storedLink] = useState(readStoredLink);

  // Opening WhatsApp on a phone often unloads this tab. A sign-in its cookie
  // still names is picked up on return, waiting or already proved, rather than
  // lost to a fresh start that would replace the cookie.
  const resumed = useQuery({
    ...whatsappStatusQuery(RESUMED),
    enabled: doorOpen,
    refetchInterval: false,
    refetchOnWindowFocus: false,
  });

  // The door can be shut between the sign-in page rendering its button and this
  // page loading, and someone can always type the path.
  const doorShut = !options.isPending && !doorOpen;
  useEffect(() => {
    if (doorShut) setLocation("/auth", { replace: true });
  }, [doorShut, setLocation]);

  const start = useMutation({
    mutationFn: async (): Promise<WhatsappStartResp> =>
      (
        await api.post<WhatsappStartResp>("/api/p/auth/whatsapp/start", {
          whatsappConsent: true,
        })
      ).data,
    onSuccess: (data) => {
      if (data.status === "started") storeLink(data.waLink);
    },
  });

  const complete = useMutation({
    mutationFn: async (): Promise<WhatsappCompleteResp> =>
      (await api.post<WhatsappCompleteResp>("/api/p/auth/whatsapp/complete")).data,
    onSuccess: (data) => {
      if (data.status !== "ok") return;
      const landing: SignInLanding = { threadId: data.onboardingThreadId ?? null };
      if (data.isNewUser) {
        setAskName(landing);
        return;
      }
      landAfterSignIn(landing, returnTo, "whatsapp");
    },
  });

  // The box was ticked for this visit, so starting again goes straight to a new link.
  const startOver = () => {
    complete.reset();
    start.mutate();
  };

  const usePhone = () => {
    setLocation(returnTo ? `/auth/phone?returnTo=${encodeURIComponent(returnTo)}` : "/auth/phone");
  };

  if (!doorOpen) return null;

  const step = () => {
    if (askName) {
      return (
        <NameStep
          statsigName="auth-whatsapp-set-name"
          onSaved={() => landAfterSignIn(askName, returnTo, "whatsapp")}
        />
      );
    }
    const startedLink = start.data?.status === "started" ? start.data.waLink : null;
    const resumedState = resumed.data?.status;
    const resuming = resumedState === "verified" || resumedState === "waiting";
    const waLink = startedLink ?? (resuming ? storedLink : null);
    if (startedLink !== null || resuming) {
      return (
        <WaitStep
          waLink={waLink}
          complete={complete}
          phoneDoorOpen={options.data?.signupEnabled === true}
          restartFailure={startFailureMessage(start.isError, start.data)}
          onStartOver={startOver}
          onUsePhone={usePhone}
          onBack={() => setLocation("/auth")}
        />
      );
    }
    return (
      <ConsentStep
        start={start}
        consent={consent}
        onConsentChange={setConsent}
        onBack={() => setLocation("/auth")}
      />
    );
  };

  return (
    <div className="flex min-h-full flex-col items-center justify-center px-6 py-10">
      <div className="flex w-full max-w-sm flex-col items-center gap-8">
        <OkThxWordmark className="h-[32px] w-[136px]" />
        {step()}
      </div>
    </div>
  );
};

type StartMutation = ReturnType<typeof useMutation<WhatsappStartResp, Error, void>>;
type CompleteMutation = ReturnType<typeof useMutation<WhatsappCompleteResp, Error, void>>;

/** The consent this sign-up records, before anything starts. */
const ConsentStep = ({
  start,
  consent,
  onConsentChange,
  onBack,
}: {
  start: StartMutation;
  consent: boolean;
  onConsentChange: (consent: boolean) => void;
  onBack: () => void;
}) => {
  const failure = startFailureMessage(start.isError, start.data);

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-col gap-2 text-center">
        <h1 className="text-2xl font-semibold">Continue with WhatsApp</h1>
        <p className="text-sm text-muted-foreground">
          You'll send okthx a short message on WhatsApp to confirm your number.
        </p>
      </div>

      <div className="flex items-start gap-2">
        <Checkbox
          id="whatsapp-consent"
          checked={consent}
          onCheckedChange={(v) => onConsentChange(v === true)}
          className="mt-0.5"
        />
        <Label
          htmlFor="whatsapp-consent"
          className="cursor-pointer select-none text-xs font-normal leading-normal text-muted-foreground"
        >
          By checking this box, you agree to receive messages from okthx on WhatsApp for account
          verification and as part of using okthx, and to our{" "}
          <a
            href="https://okthx.com/terms-of-service"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            terms of service
          </a>{" "}
          and{" "}
          <a
            href="https://okthx.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            privacy policy
          </a>
          .
        </Label>
      </div>

      {failure && <p className="text-sm text-destructive">{failure}</p>}

      <Button
        disabled={!consent || start.isPending}
        onClick={() => start.mutate()}
        className="w-full"
        data-statsig-name="auth-whatsapp-start"
      >
        {start.isPending ? "Starting..." : "Continue"}
      </Button>
      <Button
        variant="ghost"
        onClick={onBack}
        className="w-full"
        data-statsig-name="auth-whatsapp-back"
      >
        Back
      </Button>
    </div>
  );
};

/**
 * The link to send, and the wait for it to arrive. On a desktop the same link
 * shows as a QR code, so the phone that holds WhatsApp can open it. `waLink` is
 * null for a sign-in this page found already proved on load, which only needs
 * finishing.
 */
const WaitStep = ({
  waLink,
  complete,
  phoneDoorOpen,
  restartFailure,
  onStartOver,
  onUsePhone,
  onBack,
}: {
  waLink: string | null;
  complete: CompleteMutation;
  phoneDoorOpen: boolean;
  /**
   * Why starting again failed. A sign-in resumed after a reload stays on this
   * step through a failed restart, so the reason is shown here rather than on
   * the consent step it never returns to.
   */
  restartFailure: string;
  onStartOver: () => void;
  onUsePhone: () => void;
  onBack: () => void;
}) => {
  const key = waLink ?? RESUMED;
  // Once `complete` runs the proof is spent and the status reads `expired`, so
  // asking again would only contradict a sign-in that is succeeding.
  const status = useQuery({ ...whatsappStatusQuery(key), enabled: complete.isIdle });
  const state = status.data?.status;

  // `complete` spends the proof, so it runs once per sign-in: a second call
  // would find it gone and report a failure over a sign-in that worked.
  const completedFor = useRef<string | null>(null);
  const { mutate: completeSignIn } = complete;
  useEffect(() => {
    if (state !== "verified" || completedFor.current === key) return;
    completedFor.current = key;
    completeSignIn();
  }, [state, key, completeSignIn]);

  const completeFailure = verifyFailureMessage(complete.isError, complete.data);
  const startAgain = (
    <>
      {restartFailure && <p className="text-sm text-destructive">{restartFailure}</p>}
      <Button onClick={onStartOver} className="w-full" data-statsig-name="auth-whatsapp-start-over">
        Start again
      </Button>
    </>
  );

  if (completeFailure) {
    return <Outcome title="Let's try that again" message={completeFailure} action={startAgain} />;
  }

  if (complete.isPending || complete.isSuccess || state === "verified") {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <LoaderCircle aria-hidden="true" className="size-4 animate-spin" />
        Signing you in…
      </p>
    );
  }

  if (status.isError) {
    return (
      <Outcome
        title="Let's try that again"
        message="We lost track of this sign-in. Start again to get a new link."
        action={startAgain}
      />
    );
  }

  if (state === "expired") {
    return (
      <Outcome
        title="Let's try that again"
        message="This sign-in expired before your message arrived."
        action={startAgain}
      />
    );
  }

  if (state === "failed") {
    return (
      <Outcome
        title="We couldn't see your number"
        message={
          phoneDoorOpen
            ? "WhatsApp didn't share your phone number with us, so we couldn't sign you in this way. Sign up by text instead."
            : "WhatsApp didn't share your phone number with us, so we couldn't sign you in this way. Choose another way to sign in."
        }
        action={
          phoneDoorOpen ? (
            <Button
              onClick={onUsePhone}
              className="w-full"
              data-statsig-name="auth-whatsapp-use-phone"
            >
              Continue with phone
            </Button>
          ) : (
            <Button onClick={onBack} className="w-full" data-statsig-name="auth-whatsapp-back">
              Back to sign in
            </Button>
          )
        }
      />
    );
  }

  if (waLink === null) {
    // A sign-in this tab started before a reload, with no link left to show:
    // the message may already be on its way, so keep waiting, and offer a new link.
    return state === "waiting" ? (
      <div className="flex w-full flex-col items-center gap-6">
        <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <LoaderCircle aria-hidden="true" className="size-4 animate-spin" />
          Waiting for your message…
        </p>
        {restartFailure && <p className="text-sm text-destructive">{restartFailure}</p>}
        <Button
          variant="outline"
          onClick={onStartOver}
          className="w-full"
          data-statsig-name="auth-whatsapp-new-link"
        >
          Get a new link
        </Button>
      </div>
    ) : (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <LoaderCircle aria-hidden="true" className="size-4 animate-spin" />
        Checking your sign-in…
      </p>
    );
  }

  return (
    <div className="flex w-full flex-col items-center gap-6">
      <div className="flex flex-col gap-2 text-center">
        <h1 className="text-2xl font-semibold">Send us your code</h1>
        <p className="text-sm text-muted-foreground">
          Open WhatsApp and send the message we've filled in. Don't change it.
        </p>
      </div>
      <div className="hidden flex-col items-center gap-2 pointer-fine:flex">
        <div className="rounded-lg bg-white p-3">
          <QRCodeSVG value={waLink} size={176} title="Scan to open WhatsApp" />
        </div>
        <p className="text-xs text-muted-foreground">Scan with your phone's camera</p>
      </div>
      <Button asChild className="w-full" data-statsig-name="auth-whatsapp-open">
        <a href={waLink} target="_blank" rel="noopener noreferrer" className="gap-2">
          <WhatsAppIcon className="size-4" />
          Open WhatsApp
        </a>
      </Button>
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <LoaderCircle aria-hidden="true" className="size-3 animate-spin" />
        Waiting for your message…
      </p>
    </div>
  );
};

/** Where a sign-in that cannot go on stops: what happened, and the one way forward. */
const Outcome = ({
  title,
  message,
  action,
}: {
  title: string;
  message: string;
  action: React.ReactNode;
}) => (
  <div className="flex w-full flex-col gap-6">
    <div className="flex flex-col gap-2 text-center">
      <h1 className="text-2xl font-semibold">{title}</h1>
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
    {action}
  </div>
);
